"use client";

import { Minus, Plus } from "lucide-react";
import { cn } from "@/lib/cn";

type Props = {
  value: number;
  min?: number;
  max?: number;
  onChange: (next: number) => void;
  className?: string;
  label?: string;
};

export default function QuantityStepper({
  value,
  min = 1,
  max = 99,
  onChange,
  className,
  label = "Quantity",
}: Props) {
  const dec = () => onChange(Math.max(min, value - 1));
  const inc = () => onChange(Math.min(max, value + 1));

  return (
    <div
      className={cn(
        "inline-flex items-center rounded-[var(--radius-pill)] border border-[color:var(--color-border-strong)] bg-[color:var(--color-surface)]",
        className,
      )}
      role="group"
      aria-label={label}
    >
      <button
        type="button"
        onClick={dec}
        disabled={value <= min}
        aria-label="Decrease quantity"
        className="flex h-10 w-10 items-center justify-center rounded-l-[var(--radius-pill)] text-[color:var(--color-text)] hover:bg-[color:var(--color-surface-alt)] disabled:opacity-40"
      >
        <Minus size={16} />
      </button>
      <span
        aria-live="polite"
        className="min-w-[2.5rem] text-center text-sm font-medium"
      >
        {value}
      </span>
      <button
        type="button"
        onClick={inc}
        disabled={value >= max}
        aria-label="Increase quantity"
        className="flex h-10 w-10 items-center justify-center rounded-r-[var(--radius-pill)] text-[color:var(--color-text)] hover:bg-[color:var(--color-surface-alt)] disabled:opacity-40"
      >
        <Plus size={16} />
      </button>
    </div>
  );
}
